import {
  getReportByPage,
  type ReportRequestOptions,
} from '@services/api/reports/byPage.api'
import type {
  ReportQueryParams,
  ReportKpiWithSeriesResult,
  ReportPeriodSeriesResult,
} from '@services/api/reports/finance.api'

export interface ProductionPerformanceReportResult {
  production: ReportKpiWithSeriesResult
  productionGoal: ReportKpiWithSeriesResult
  avgProductionPerHour: ReportKpiWithSeriesResult
  avgProductionPerDay: ReportKpiWithSeriesResult
}

export interface CollectionsFinancialEfficiencyReportResult {
  collections: ReportKpiWithSeriesResult
  production: ReportKpiWithSeriesResult
  netCollectionPercentage: ReportKpiWithSeriesResult
  sameDayPayments: ReportKpiWithSeriesResult
}

export interface ClinicalMixTreatmentProfileReportResult {
  totalProcedures: ReportKpiWithSeriesResult
  crownPercentage: ReportKpiWithSeriesResult
  srpPercentage: ReportKpiWithSeriesResult
  highValueProcedurePercentage: ReportKpiWithSeriesResult
  crownProcedures: ReportKpiWithSeriesResult
  srpProcedures: ReportKpiWithSeriesResult
  fillingProcedures: ReportKpiWithSeriesResult
  extractionProcedures: ReportKpiWithSeriesResult
}

export interface TreatmentEffectivenessReportResult {
  acceptanceRate: ReportKpiWithSeriesResult
  completedVsDiagnosedRate: ReportKpiWithSeriesResult
  totalDiagnosed: ReportKpiWithSeriesResult
  pendingTreatments: ReportKpiWithSeriesResult
  statusDistribution: ReportPeriodSeriesResult
}

export interface EfficiencyScorecardProviderRow {
  providerId: string
  providerName: string
  production: number
  productionPerHour: number
  efficiencyScore: number
  chairUtilization: number
}

export interface EfficiencyScorecardReportResult {
  efficiencyScore: ReportKpiWithSeriesResult
  productionPerHour: ReportKpiWithSeriesResult
  chairUtilization: ReportKpiWithSeriesResult
  totalProduction: ReportKpiWithSeriesResult
  providers: EfficiencyScorecardProviderRow[]
}

export const getProductionPerformanceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<ProductionPerformanceReportResult> =>
  getReportByPage<ProductionPerformanceReportResult>(
    '/provider-performance/production-performance',
    params,
    options,
  )

export const getCollectionsFinancialEfficiencyReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<CollectionsFinancialEfficiencyReportResult> =>
  getReportByPage<CollectionsFinancialEfficiencyReportResult>(
    '/provider-performance/collections-financial-efficiency',
    params,
    options,
  )

export const getClinicalMixTreatmentProfileReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<ClinicalMixTreatmentProfileReportResult> =>
  getReportByPage<ClinicalMixTreatmentProfileReportResult>(
    '/provider-performance/clinical-mix-treatment-profile',
    params,
    options,
  )

export const getTreatmentEffectivenessReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<TreatmentEffectivenessReportResult> =>
  getReportByPage<TreatmentEffectivenessReportResult>(
    '/provider-performance/treatment-effectiveness',
    params,
    options,
  )

export const getEfficiencyScorecardReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<EfficiencyScorecardReportResult> =>
  getReportByPage<EfficiencyScorecardReportResult>(
    '/provider-performance/efficiency-scorecard',
    params,
    options,
  )
